export const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

/**
 * Snap to whole cents.
 *
 * Money is held as doubles, so a chain of adds and subtracts that should land on
 * zero often lands on -2.27e-13 instead. `fmt` hides that when printing, but any
 * `< 0` / `> 0` test on the raw value still sees a negative number — which is how
 * a fully-assigned envelope ended up flagged red with "-$0.00" left over.
 * Round before comparing, always.
 */
export const cents = (n = 0) => Math.round(((n || 0) + Number.EPSILON) * 100) / 100 || 0;

export const fmt = (n = 0) => {
  // Round to 2 dp to eliminate floating-point noise, then strip -0
  const clean = Math.round((n || 0) * 100) / 100 || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(clean);
};

export const fmtPct = (n = 0) => `${(n * 100).toFixed(1)}%`;

export const monthLabel = (m) => m ? `${MONTH_NAMES[m.month]} ${m.year}` : '';

export const groupBy = (arr, key) =>
  arr.reduce((acc, item) => {
    const g = item[key] || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(item);
    return acc;
  }, {});

export const sumBy = (arr, field) =>
  arr.reduce((s, x) => s + (x[field] || 0), 0);

/** Future value of lump sum: FV = PV * (1 + r)^n */
export const fvLump = (pv, rAnnual, months) =>
  pv * Math.pow(1 + rAnnual / 12, months);

/** Future value of recurring payment series: FV = PMT * [(1+r)^n - 1] / r */
export const fvSeries = (pmt, rAnnual, months) => {
  const r = rAnnual / 12;
  if (r === 0) return pmt * months;
  return pmt * (Math.pow(1 + r, months) - 1) / r;
};

/** Months between today and a target date */
export const monthsUntil = (targetDate) => {
  if (!targetDate) return 0;
  const today = new Date();
  const target = new Date(targetDate);
  return Math.max(0,
    (target.getFullYear() - today.getFullYear()) * 12 +
    (target.getMonth() - today.getMonth()));
};

/** Monthly payment needed to reach a goal via savings + growth */
export const monthlyNeeded = (goal, currentBalance, rAnnual, months) => {
  if (months <= 0) return 0;
  const r = rAnnual / 12;
  const fv = fvLump(currentBalance, rAnnual, months);
  const remaining = goal - fv;
  if (remaining <= 0) return 0;
  if (r === 0) return remaining / months;
  return remaining * r / (Math.pow(1 + r, months) - 1);
};

/** Extra monthly mortgage principal needed to payoff by target date */
export const extraMortgagePayment = (principal, rate, monthlyPmt, escrow, months) => {
  if (months <= 0 || principal <= 0) return 0;
  const r = rate / 12;
  if (r === 0) return Math.max(0, principal / months - monthlyPmt);
  // Standard amortisation: need payment P such that PV annuity = principal
  const pmt = principal * r / (1 - Math.pow(1 + r, -months));
  return Math.max(0, pmt - (monthlyPmt - escrow));
};
